import { db, type ImportRecord, type Item, type Room, type Section } from '../db/schema';
import { writeZip, type ZipInput } from './zip';
import {
  backupName,
  DATA,
  FORMAT,
  imageFile,
  MANIFEST,
  SCHEMA_VERSION,
  thumbFile,
  type Backup,
  type Counts,
  type ImageMeta,
  type Manifest,
} from './format';

/**
 * Everything the app holds, as one file that outlives this browser profile.
 *
 * Nothing here writes to the database, so an export can't lose anything it
 * fails halfway through.
 */

export type BackupFile = {
  blob: Blob;
  name: string;
  counts: Counts;
};

/**
 * `itemIds` narrows it to a selection — the multi-select export. The rooms and
 * sections those items live in come along, so restoring puts them back where
 * they were rather than in a heap.
 */
export async function buildBackup(itemIds?: readonly string[]): Promise<BackupFile> {
  const at = Date.now();
  const data = await collect(itemIds);
  const images = await db.images.bulkGet(imageIdsIn(data));

  const meta: ImageMeta[] = [];
  const files: ZipInput[] = [];

  for (const image of images) {
    // A reference to a row that isn't there is a bug, not a reason to refuse
    // the export — the rest of the backup is still worth having, and restore
    // drops the dangling reference on the way in.
    if (image === undefined) continue;

    const file = imageFile(image.id, image.mime);
    const thumb = thumbFile(image.id);

    meta.push({
      id: image.id,
      w: image.w,
      h: image.h,
      mime: image.mime,
      addedAt: image.addedAt,
      file,
      thumbFile: thumb,
    });

    files.push({ name: file, data: image.blob }, { name: thumb, data: image.thumb });
  }

  const backup: Backup = { ...data, images: meta };

  const counts: Counts = {
    rooms: backup.rooms.length,
    sections: backup.sections.length,
    items: backup.items.length,
    images: backup.images.length,
    imports: backup.imports.length,
  };

  const manifest: Manifest = {
    format: FORMAT,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: at,
    complete: itemIds === undefined,
    counts,
  };

  // The manifest leads so that anything reading the file as a stream knows
  // what it's holding before the photos start.
  const blob = await writeZip([
    { name: MANIFEST, data: json(manifest) },
    { name: DATA, data: json(backup) },
    ...files,
  ]);

  return { blob, name: backupName(at, itemIds !== undefined), counts };
}

type Records = {
  rooms: Room[];
  sections: Section[];
  items: Item[];
  imports: ImportRecord[];
};

async function collect(itemIds: readonly string[] | undefined): Promise<Records> {
  const [rooms, sections, items, imports] = await Promise.all([
    db.rooms.toArray(),
    db.sections.toArray(),
    db.items.toArray(),
    db.imports.toArray(),
  ]);

  if (itemIds === undefined) return { rooms, sections, items, imports };

  const wanted = new Set(itemIds);
  const chosen = items.filter((item) => wanted.has(item.id));

  const roomIds = new Set(chosen.map((item) => item.roomId));
  const sectionIds = ancestors(
    chosen.flatMap((item) => (item.sectionId === undefined ? [] : [item.sectionId])),
    sections,
  );

  return {
    rooms: rooms.filter((room) => roomIds.has(room.id)),
    sections: sections.filter((section) => sectionIds.has(section.id)),
    items: chosen,
    imports: imports.filter((record) => wanted.has(record.itemId)),
  };
}

/** A section is no use in a backup without the sections it sits inside. */
function ancestors(ids: readonly string[], sections: readonly Section[]): Set<string> {
  const byId = new Map(sections.map((section) => [section.id, section]));
  const found = new Set<string>();

  for (const id of ids) {
    let at: string | undefined = id;
    // A parent chain that somehow loops would otherwise spin here.
    while (at !== undefined && !found.has(at)) {
      found.add(at);
      at = byId.get(at)?.parentId;
    }
  }

  return found;
}

function imageIdsIn(data: Records): string[] {
  return [
    ...new Set([
      ...data.rooms.flatMap((room) => (room.coverImageId === undefined ? [] : [room.coverImageId])),
      ...data.sections.flatMap((section) =>
        section.coverImageId === undefined ? [] : [section.coverImageId],
      ),
      ...data.items.flatMap((item) => [
        ...(item.coverImageId === undefined ? [] : [item.coverImageId]),
        ...item.imageIds,
      ]),
    ]),
  ];
}

function json(value: unknown): Blob {
  return new Blob([JSON.stringify(value)], { type: 'application/json' });
}
